import DOMPurify from 'dompurify'
import type { InboxEmailBody } from '@hub/contracts'
import { markEmailQuotes, type ForwardLabels, type HtmlQuote } from './email-quotes-html'

/**
 * Rendering an email's HTML (board eeqo). Everything a reviewer needs is here:
 * the sanitizer profile, the one URL rewrite, the CSS rewrite and the CSP.
 *
 * The HTML is hostile by default. Three independent layers:
 *  1. the frame's `sandbox` (EMAIL_FRAME_SANDBOX) has no `allow-scripts`, so
 *     nothing in the mail runs. `allow-same-origin` is what lets the parent
 *     measure the frame and read its selection — and what makes the CSP below
 *     necessary, because same-origin requests from the frame carry the session;
 *  2. DOMPurify plus the rewrites: only our attachment route, `data:` pictures
 *     and (after "Visa bilder") https third parties are ever referenced;
 *  3. a CSP inside the srcdoc: nothing loads but pictures from THIS email's
 *     attachment path, `data:`, and https in remote mode. CSS is kept (mail is
 *     unreadable without it) and cannot be parsed reliably with regexes, so the
 *     CSP is the wall, and the CSS rewrite is what makes the mail look right.
 */

/** The frame's sandbox. Never add `allow-scripts`: with same-origin it is no sandbox at all. */
export const EMAIL_FRAME_SANDBOX = 'allow-same-origin allow-popups allow-popups-to-escape-sandbox'

/** DOMPurify profile, frozen by email-html.test.ts. */
export const EMAIL_PURIFY_CONFIG = Object.freeze({
  USE_PROFILES: { html: true },
  // Keeps <style> from <head> and the attributes of <body>: both are the mail's
  // design. (WHOLE_DOCUMENT would give the same, but we rebuild the document.)
  FORCE_BODY: true,
  RETURN_DOM: true,
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: true,
  FORBID_TAGS: [
    'script',
    'iframe',
    'frame',
    'frameset',
    'object',
    'embed',
    'applet',
    'form',
    'input',
    'button',
    'select',
    'textarea',
    'meta',
    'link',
    'base',
    'title',
    'noscript',
    'noembed',
    'noframes',
    'xmp',
    'plaintext',
    'svg',
    'math',
    'video',
    'audio',
    'source',
    'track',
    'canvas',
    'template',
    'slot',
    'dialog',
  ],
  ADD_FORBID_CONTENTS: ['title', 'select', 'textarea', 'canvas', 'template', 'object', 'applet'],
  FORBID_ATTR: ['srcset', 'sizes', 'ping', 'formaction', 'action', 'poster'],
})

const purify = DOMPurify(window)

// Runs before DOMPurify's own checks, which would drop the whole element for
// Outlook's `<style><!-- … --></style>` (markup-looking text in a raw element).
purify.addHook('uponSanitizeElement', (node, data) => {
  if (data.tagName === 'style') node.textContent = normalizeCss(node.textContent ?? '')
})

/** 1×1 transparent GIF: what a blocked picture's src becomes. */
export const BLOCKED_IMAGE_SRC =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

export type ImageRewrite =
  | { action: 'keep'; url: string; attachmentIndex?: number }
  | { action: 'block' }
  | { action: 'drop' }

export interface EmailImageContext {
  inlineImages: InboxEmailBody['inlineImages']
  /** The attachment route's URL for an attachment of this email (inline rendering). */
  attachmentUrl: (index: number) => string
  /** "Visa bilder" was pressed for this open of this email. */
  remoteImages: boolean
}

/** `cid:logo%40x` → the Content-ID it names. */
function cidOf(url: string): string {
  const raw = url.slice('cid:'.length)
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

/**
 * THE url rewrite: every picture reference of a mail goes through here, from
 * `src`, `background` and CSS alike. An allowlist on purpose — this is what
 * stops `<img src="/some/get/endpoint">` from running as the logged-in member.
 */
export function rewriteImageUrl(rawUrl: string, context: EmailImageContext): ImageRewrite {
  // Browsers strip these before parsing a URL, so `ht\ntp:` is `http:`.
  // eslint-disable-next-line no-control-regex
  const url = rawUrl.replace(/[\u0000- \u007f-\u009f]/g, '')
  if (/^cid:/i.test(url)) {
    const image = context.inlineImages.find((i) => i.cid === cidOf(url))
    return image
      ? {
          action: 'keep',
          url: context.attachmentUrl(image.index),
          attachmentIndex: image.index,
        }
      : { action: 'drop' }
  }
  if (/^data:image\//i.test(url)) return { action: 'keep', url }
  if (/^https?:\/\//i.test(url)) {
    let parsed: URL
    try {
      parsed = new URL(url)
    } catch {
      return { action: 'drop' }
    }
    if (parsed.origin === window.location.origin) return { action: 'drop' }
    parsed.protocol = 'https:'
    return context.remoteImages ? { action: 'keep', url: parsed.href } : { action: 'block' }
  }
  return { action: 'drop' }
}

// ── CSS ──────────────────────────────────────────────────────────────────────

/** `\75 rl(` is `url(`: CSS escapes resolved, so the rules below see the real text. */
function decodeCssEscapes(css: string): string {
  return css.replace(/\\(\\)|\\([0-9a-f]{1,6})\s?|\\([a-z])/gi, (match, slash, hex, letter) => {
    if (slash) return match
    if (letter) return letter as string
    const code = parseInt(hex as string, 16)
    return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : ''
  })
}

/**
 * Plain CSS with nothing that can leave its context. The text ends up raw
 * inside `<style>` in the srcdoc, so `<` must never survive: decoding
 * `\3c/style\3e` above would otherwise close the element.
 */
export function normalizeCss(css: string): string {
  return decodeCssEscapes(css)
    .replace(/\/\*[\s\S]*?(\*\/|$)/g, '')
    .replace(/<!--|-->/g, '')
    .replace(/</g, '')
    .replace(/@(import|charset|namespace)\b[^;]*(;|$)/gi, '')
    .replace(/@font-face\s*\{[^}]*(\}|$)/gi, '')
    .replace(/(?:-moz-binding|behavior)\s*:[^;}]*/gi, '')
    .replace(/\bexpression\s*\(/gi, 'none(')
    .replace(/(?:-webkit-)?\bimage-set\s*\([^;}]*/gi, 'none')
    .replace(/\bsrc\s*\([^;}]*/gi, 'none')
}

/** `url()` tokens through `rewriteImageUrl`. Input: `normalizeCss` output. */
function rewriteCssUrls(css: string, visit: (url: string) => ImageRewrite): string {
  // The closing quote and parenthesis are optional: CSS closes them at the end of input.
  return css.replace(/\burl\(\s*(?:"([^"]*)"?|'([^']*)'?|([^)]*))\s*\)?/gi, (_m, dq, sq, bare) => {
    const rewrite = visit(((dq ?? sq ?? bare) as string).trim())
    if (rewrite.action === 'drop') return 'none'
    return `url("${rewrite.action === 'keep' ? rewrite.url : BLOCKED_IMAGE_SRC}")`
  })
}

// ── Sanitize ─────────────────────────────────────────────────────────────────

/** Declared colors = a designed mail; it needs the white surface it was designed on. */
const CSS_COLOR_RE =
  /(?:^|[;{\s])(?:background(?:-color|-image)?|color)\s*:(?!\s*(?:inherit|initial|unset|transparent|currentcolor)\b)/i

function declaresColors(root: HTMLElement): boolean {
  const elements = [root, ...root.querySelectorAll<HTMLElement>('*')]
  return elements.some(
    (el) =>
      el.hasAttribute('bgcolor') ||
      el.hasAttribute('background') ||
      (el.tagName === 'FONT' && el.hasAttribute('color')) ||
      CSS_COLOR_RE.test(el.getAttribute('style') ?? '') ||
      (el.tagName === 'STYLE' && CSS_COLOR_RE.test(el.textContent ?? ''))
  )
}

const SAFE_LINK_RE = /^(https?:|mailto:|tel:)/i

function sanitizeLink(link: Element): void {
  const href = link.getAttribute('href')
  if (href === null) return
  // eslint-disable-next-line no-control-regex
  if (!SAFE_LINK_RE.test(href.replace(/[\u0000- \u007f-\u009f]/g, ''))) {
    // Also fragments: a srcdoc resolves `#x` against the app's URL, not itself,
    // so it would load the app in the frame instead of scrolling.
    link.removeAttribute('href')
    link.removeAttribute('target')
    return
  }
  link.setAttribute('target', '_blank')
  link.setAttribute('rel', 'noopener noreferrer nofollow')
}

export interface SanitizedEmail {
  /** Sanitized `<body>`, detached. */
  body: HTMLElement
  /** Pictures replaced by the placeholder until "Visa bilder". */
  blockedImages: number
  /** Indexes of the attachments rendered as `cid:` pictures. */
  usedCids: number[]
  /** The mail declares colors: render on white paper in both themes. */
  paper: boolean
  /** Quoted history and forwards, already marked in `body`. */
  quote: HtmlQuote
}

export interface EmailQuoteContext {
  subject: string | null
  labels: ForwardLabels
}

export function sanitizeEmailHtml(
  html: string,
  context: EmailImageContext,
  quotes: EmailQuoteContext
): SanitizedEmail {
  const body = purify.sanitize(html, EMAIL_PURIFY_CONFIG as never) as unknown as HTMLElement
  const paper = declaresColors(body)
  let blockedImages = 0
  const usedCids = new Set<number>()

  const visit = (url: string): ImageRewrite => {
    const rewrite = rewriteImageUrl(url, context)
    if (rewrite.action === 'block') blockedImages++
    if (rewrite.action === 'keep' && rewrite.attachmentIndex !== undefined) {
      usedCids.add(rewrite.attachmentIndex)
    }
    return rewrite
  }

  for (const el of [body, ...body.querySelectorAll<HTMLElement>('*')]) {
    // Class names of ours (`hub-quote`, …) mean something in the frame's CSS.
    if (/\bhub-/.test(el.getAttribute('class') ?? '')) {
      const classes = (el.getAttribute('class') ?? '').split(/\s+/)
      el.setAttribute('class', classes.filter((c) => !c.startsWith('hub-')).join(' '))
    }
    if (el.tagName === 'A' || el.tagName === 'AREA') sanitizeLink(el)
    if (el.tagName === 'IMG') {
      const src = el.getAttribute('src')
      const rewrite: ImageRewrite = src === null ? { action: 'drop' } : visit(src)
      if (rewrite.action === 'drop') {
        el.remove()
        continue
      }
      if (rewrite.action === 'keep') {
        el.setAttribute('src', rewrite.url)
      } else {
        el.setAttribute('src', BLOCKED_IMAGE_SRC)
        el.setAttribute('data-hub-blocked', '')
      }
    }
    const background = el.getAttribute('background')
    if (background !== null) {
      const rewrite = visit(background)
      if (rewrite.action === 'keep') el.setAttribute('background', rewrite.url)
      else el.removeAttribute('background')
    }
    const style = el.getAttribute('style')
    if (style !== null) el.setAttribute('style', rewriteCssUrls(normalizeCss(style), visit))
    if (el.tagName === 'STYLE') el.textContent = rewriteCssUrls(el.textContent ?? '', visit)
  }

  // After the walk above: it is what takes our class names away from the sender.
  const quote = markEmailQuotes(body, quotes.subject, quotes.labels)
  return { body, blockedImages, usedCids: [...usedCids], paper, quote }
}

// ── The frame's document ─────────────────────────────────────────────────────

export type EmailTheme = 'light' | 'dark'

/** The app's tokens for the frame: CSS variables do not cross into it (web/globals.css). */
const THEME_COLORS: Readonly<Record<EmailTheme, { fg: string; link: string }>> = {
  light: { fg: '#191919', link: '#2563eb' },
  dark: { fg: '#e3e3e3', link: '#60a5fa' },
}

/** Directory of an attachment URL: the only path the frame may load pictures from. */
function attachmentDirectory(attachmentUrl: (index: number) => string): string {
  const url = new URL(attachmentUrl(0), window.location.origin)
  return `${url.origin}${url.pathname.slice(0, url.pathname.lastIndexOf('/') + 1)}`
}

/** The frame's CSP. A `<meta>` cannot carry `sandbox`: the iframe attribute does. */
export function emailFrameCsp(
  attachmentUrl: (index: number) => string,
  remoteImages: boolean
): string {
  return [
    "default-src 'none'",
    `img-src ${attachmentDirectory(attachmentUrl)} data:${remoteImages ? ' https:' : ''}`,
    "style-src 'unsafe-inline'",
    "font-src 'none'",
    "form-action 'none'",
    "base-uri 'none'",
    "frame-src 'none'",
    "object-src 'none'",
    "script-src 'none'",
  ].join('; ')
}

function frameCss(theme: EmailTheme, paper: boolean, showQuoted: boolean): string {
  const colors = paper ? { fg: '#000000', link: '#1a56c4' } : THEME_COLORS[theme]
  return `
:root { color-scheme: ${paper ? 'light' : theme}; }
html { margin: 0; overflow-x: auto; overflow-y: hidden; }
body {
  margin: 0;
  padding: ${paper ? '16px' : '0'};
  display: flow-root;
  background: ${paper ? '#ffffff' : 'transparent'};
  color: ${colors.fg};
  font: 14px/1.6 -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  overflow-wrap: break-word;
}
a { color: ${colors.link}; }
img { max-width: 100%; }
img:not([data-hub-blocked]) { height: auto; }
/* The placeholder is 1×1: scaled to the width the mail asks for it would be a square. */
img[data-hub-blocked]:not([height]) { height: 40px !important; }
img[data-hub-blocked] { background: #8881; }
.hub-quote { margin-top: 12px; padding-left: 12px; border-left: 2px solid #8a8a8a; }
${showQuoted ? '' : '.hub-quote { display: none !important; }'}
.hub-fwd { margin-top: 12px; padding: 10px 12px; border: 1px solid #8a8a8a; border-radius: 6px; }
.hub-fwd > blockquote { margin: 0; }
.hub-fwd-title { margin: 0 0 8px; font-size: 12px; font-weight: 500; opacity: 0.7; }
.hub-fwd-head {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2px 12px;
  margin: 0 0 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #8a8a8a;
}
.hub-fwd-head dt { opacity: 0.7; }
.hub-fwd-head dd { margin: 0; min-width: 0; }
`.trim()
}

export interface SrcdocOptions {
  theme: EmailTheme
  paper: boolean
  remoteImages: boolean
  showQuoted: boolean
  attachmentUrl: (index: number) => string
}

const escapeAttribute = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')

/** The whole document of the frame: CSP first, so it covers everything after it. */
export function buildSrcdoc(body: HTMLElement, options: SrcdocOptions): string {
  const csp = emailFrameCsp(options.attachmentUrl, options.remoteImages)
  return [
    '<!doctype html><html><head><meta charset="utf-8">',
    `<meta http-equiv="Content-Security-Policy" content="${escapeAttribute(csp)}">`,
    // The iframe's `referrerpolicy` covers loading the frame, not the pictures in it.
    '<meta name="referrer" content="no-referrer">',
    `<style>${frameCss(options.theme, options.paper, options.showQuoted)}</style>`,
    `</head>${body.outerHTML}</html>`,
  ].join('')
}
